import {
    findIndex as _findIndex,
    get as _get,
    isEmpty as _isEmpty,
} from 'lodash';

import {convertToInt} from '@utils/helpers';
import {getGeoRestrictionsForDeviceCategories} from '@utils/countryHelpers';
import {
    DEVICE_CATEGORY_DESKTOP,
    DEVICE_CATEGORY_MOBILE,
    DEVICE_CATEGORY_SMART_TV,
} from '@constants/variables';
import {PreFillGeoRestrictions} from '@graphql/eventBooking/query';
import GeoRestrictionsForm from '@modules/geoRestriction/forms/GeoRestrictionsForm';
import {
    checkSubdivisionsAnyCountry,
    getDeviceNameById,
    getFieldNamesByDeviceId,
    setCountryRestrictionsForDevices,
    setCountryEntityGeoRestrictions,
    setSubdivisionsEntityGeoRestrictions,
    setSubdivisionsRestrictionsForDevices, prepareForHandleSwitchChanged,
} from '@modules/events/utils/eventsBookingGeoRestriction';

export class EventBookingGeoRestrictionsForm extends GeoRestrictionsForm {
    countryTreeOptions = null;
    subdivisionTreeOptions = null;
    ARRAY_WITH_DEVICES = [DEVICE_CATEGORY_DESKTOP, DEVICE_CATEGORY_MOBILE, DEVICE_CATEGORY_SMART_TV];

    prepareDataForSubmit = (data) => {
        const preparedData = this.props.Model.editForm
            ? this.props.stepsMethods.getForms()['step1'].formValues
            : this.props.previousStepData;

        return {
            id: convertToInt(this.props.formParams.id),
            booking_type: convertToInt(preparedData.booking_type),
            client: convertToInt(preparedData.client),
            distribution: convertToInt(preparedData.distribution),
            client_package: preparedData.client_package ? convertToInt(preparedData.client_package) : null,
            price: null === preparedData.price ? null : parseFloat(preparedData.price),
            auto_update_price: null === preparedData.auto_update_price ? false : preparedData.auto_update_price,
            auto_update_geo_restrictions: preparedData.auto_update_geo_restrictions || false,
            invoice_status: !preparedData.invoice_status_toggle ? convertToInt(preparedData.invoice_status) : null,
            booking_geo_restrictions: getGeoRestrictionsForDeviceCategories(data),
        };
    };

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps, prevState);

        const {
                GraphQLOptionsData: { continents, countries, distribution },
                formValues,
            } = this.props,
            entityData = _get(this.props, 'GraphQLEntityData', {}),
            restrictions = !_isEmpty(entityData) ? this.getEntityGeoRestrictions(entityData) : {},
            desktopRestrictions = this.getDeviceCategoryRestrictions(restrictions, DEVICE_CATEGORY_DESKTOP.toString()),
            mobileRestrictions = this.getDeviceCategoryRestrictions(restrictions, DEVICE_CATEGORY_MOBILE.toString()),
            smartTvRestrictions = this.getDeviceCategoryRestrictions(restrictions, DEVICE_CATEGORY_SMART_TV.toString());

        const entityDataChanged = prevProps.GraphQLEntityData !== this.props.GraphQLEntityData;
        const formValuesChanged = prevProps.formValues !== this.props.formValues;

        if (entityDataChanged) {
            const devices = [
                {
                    deviceCategoryId: DEVICE_CATEGORY_DESKTOP,
                    restrictionIndex: desktopRestrictions,
                    stateFlagName: 'desktopCountriesGenerated',
                },
                {
                    deviceCategoryId: DEVICE_CATEGORY_MOBILE,
                    restrictionIndex: mobileRestrictions,
                    stateFlagName: 'mobileCountriesGenerated',
                },
                {
                    deviceCategoryId: DEVICE_CATEGORY_SMART_TV,
                    restrictionIndex: smartTvRestrictions,
                    stateFlagName: 'smartTvCountriesGenerated',
                },
            ];

            setCountryRestrictionsForDevices({
                devices: devices,
                restrictions: restrictions,
                continents: continents,
                state: this.state,
                setState: this.setState,
                setField: this.setField,
            });

            const deviceByRestriction = {
                [DEVICE_CATEGORY_DESKTOP]: desktopRestrictions,
                [DEVICE_CATEGORY_MOBILE]: mobileRestrictions,
                [DEVICE_CATEGORY_SMART_TV]: smartTvRestrictions,
            };

            setSubdivisionsRestrictionsForDevices({
                DEVICE_CATEGORIES: this.ARRAY_WITH_DEVICES,
                deviceByRestriction: deviceByRestriction,
                restrictions: restrictions,
                countries: countries,
                state: this.state,
                setState: this.setState,
                setField: this.setField,
            });
        }

        if (!this.state.deviceCategoriesGenerated && distribution) {
            let isChanged = false;

            this.ARRAY_WITH_DEVICES.forEach((deviceCategoryId) => {
                if (-1 === _findIndex(
                    distribution.product.device_categories,
                    {'id': deviceCategoryId.toString()}
                )) {
                    this.setField(`${getDeviceNameById(deviceCategoryId)}_switch`, {
                        hidden: true,
                    });
                } else if (!isChanged) {
                    isChanged = true;

                    this.setField(`${getDeviceNameById(deviceCategoryId)}_switch`, {
                        divClassName: null,
                    });
                }
            });
            this.setState(() => ({deviceCategoriesGenerated: true}));
        }

        if (!this.state.preFillGeoRestrictionsGenerated
            && !this.props.formParams.id
            && countries
            && continents
            && this.props.previousStepData?.distribution
            && this.props.previousStepData?.client_package) {

            this.getPreFillGeoRestrictions(
                convertToInt(this.props.previousStepData.distribution),
                convertToInt(this.props.previousStepData.client_package),
                {continents: continents, countries: countries}
            );

            this.setState(() => ({preFillGeoRestrictionsGenerated: true}));
        }

        if (formValuesChanged) {
            const devicesSwitchValues = {
                [DEVICE_CATEGORY_DESKTOP]: formValues.desktop_switch,
                [DEVICE_CATEGORY_MOBILE]: formValues.mobile_switch,
                [DEVICE_CATEGORY_SMART_TV]: formValues.smart_tv_switch,
            };

            this.ARRAY_WITH_DEVICES.forEach((deviceId) => {
                const {switchFieldName, dropdownCountriesFieldName, dropdownSubdivisionsFieldName} =
                    getFieldNamesByDeviceId(deviceId);

                this.setOnChangeCallback({
                    [switchFieldName]: (data) => this.handleSwitchChanged(deviceId, data.value),
                });

                this.setCountriesOnChangeCallback(
                    countries,
                    dropdownCountriesFieldName,
                    dropdownSubdivisionsFieldName
                );
            });

            this.setField('geo_restrictions_error_content', {
                hidden: Object.values(devicesSwitchValues).every((value) => false !== value),
            });
        }

        if (this.props !== prevProps) {
            this.props.stepsMethods.setForm('step2', this.props);
        }
    }

    getPreFillGeoRestrictions = (distributionId, clientPackageId, data) => {
        this.setState(() => ({defaultForm_loading: true}));

        return this.props.client.query({
            query: PreFillGeoRestrictions,
            fetchPolicy: 'network-only',
            variables: {
                distribution: distributionId,
                clientPackage: clientPackageId,
            },
        }).then((response) => {
            const preFillGeoRestrictions = response.data.preFillGeoRestrictions;

            preFillGeoRestrictions.forEach((geoRestriction) => {
                setCountryEntityGeoRestrictions(convertToInt(geoRestriction.device_category), {
                    continents: data.continents,
                    geoRestrictions: geoRestriction.countries,
                }, this.setField);

                if (checkSubdivisionsAnyCountry(data.countries, geoRestriction.countries)) {
                    setSubdivisionsEntityGeoRestrictions(convertToInt(geoRestriction.device_category), {
                        countries: data.countries,
                        countriesGeoRestrictions: geoRestriction.countries,
                        subdivisionsGeoRestrictions: geoRestriction.subdivisions || [],
                    }, this.setField);
                }
            });
        }).catch((response) => {
            this.props.MessageBox
                .addMessage('formInnerErrorMessage',
                    "Can't get pre-fill geo restrictions for selected distribution and client package.",
                    response.message, 'error');
        }).finally(
            () => {
                this.setState(() => ({defaultForm_loading: false}));
            }
        );
    };

    handleSwitchChanged (deviceId, value) {
        const result = prepareForHandleSwitchChanged({
            props: this.props,
            setField: this.setField,
            deviceId: deviceId,
            value: value,
            initialCountryTreeOptions: this.countryTreeOptions,
            initialSubdivisionTreeOptions: this.subdivisionTreeOptions,
        });

        this.countryTreeOptions ??= result.countryTreeOptions;
        this.subdivisionTreeOptions ??= result.subdivisionTreeOptions;
    }

    getEntityGeoRestrictions = (entityData) => {
        return _get(entityData, 'booking.booking_geo_restrictions', {});
    };

    getDeviceCategoryRestrictions = (restrictions, deviceCategory) => {
        return _findIndex(restrictions, {'device_category': {'id': deviceCategory}});
    };
}
